import { HttpClient, HttpHeaders } from "@angular/common/http";
import { Injectable } from "@angular/core";
import { Observable } from "rxjs";
import { map } from "rxjs/operators";
import { v4 as uuidv4 } from "uuid";
import { environment } from "../../../environments/environment";
import {
  AccessCatalog,
  AgentGroupMemberCandidate,
  ApiEnvelope,
} from "./access-management.models";
import { AgentGroup } from "../generated/waba-api/models";

@Injectable({ providedIn: "root" })
export class AccessManagementService {
  private readonly baseUrl = `${environment.wabaApiUrl}/v1/api/ui`;

  constructor(private readonly http: HttpClient) {}

  getCatalog(): Observable<ApiEnvelope<AccessCatalog>> {
    return this.http.get<ApiEnvelope<AccessCatalog>>(
      `${this.baseUrl}/access-catalog`,
      { headers: this.headers() },
    ).pipe(map((response) => ({ ...response, body: sanitizeAccessCatalog(response.body) })));
  }

  getMemberCandidates(): Observable<ApiEnvelope<AgentGroupMemberCandidate[]>> {
    return this.http.get<ApiEnvelope<AgentGroupMemberCandidate[]>>(
      `${this.baseUrl}/agent-groups/member-candidates`,
      { headers: this.headers() },
    );
  }

  getCurrentGroup(): Observable<ApiEnvelope<string | null>> {
    return this.http.get<ApiEnvelope<string | null>>(
      `${this.baseUrl}/agent-groups/current`,
      { headers: this.headers() },
    );
  }

  getAgentGroupOptions(companyId?: string): Observable<ApiEnvelope<AgentGroup[]>> {
    const query = companyId ? `?companyId=${encodeURIComponent(companyId)}` : "";
    return this.http.get<ApiEnvelope<AgentGroup[]>>(
      `${this.baseUrl}/agent-groups/options${query}`,
      { headers: this.headers() },
    );
  }

  private headers(): HttpHeaders {
    return new HttpHeaders({
      "x-api-requestId": uuidv4(),
      "x-api-canal": "web",
    });
  }
}

const PROTECTED_SOLUTION_ROLES = new Set([
  "ADMIN_SOLUTION",
  "ADMIN_SOLUTION_UI",
  "ADMIN_CONVERSATIONS",
  "DASHY_COMPANIES",
  "MANAGE_PAYMENT_CONFIG",
  "PARCELS",
  "MERCHANT",
  "MANAGE_DELIVERYMAN",
]);

export function sanitizeAccessCatalog(catalog: AccessCatalog | null | undefined): AccessCatalog {
  const isProtectedRole = (role: string): boolean =>
    PROTECTED_SOLUTION_ROLES.has((role || "").trim().toUpperCase())
    || (role || "").trim().toUpperCase().includes("SOLUTION");
  const isProtectedGroup = (name?: string, path?: string): boolean => {
    const normalizedName = (name || "").trim().toUpperCase();
    const normalizedPath = (path || "").trim().replace(/\\/g, "/").toUpperCase();
    return normalizedName === "SOLUTION"
      || normalizedName.startsWith("SOLUTION_")
      || normalizedPath === "/SOLUTION"
      || normalizedPath.startsWith("/SOLUTION/");
  };

  return {
    roles: (catalog?.roles || []).filter((role) => !isProtectedRole(role)),
    groups: (catalog?.groups || [])
      .filter((group) => !isProtectedGroup(group.name, group.path))
      .map((group) => ({
        ...group,
        roles: (group.roles || []).filter((role) => !isProtectedRole(role)),
      }))
      .filter((group) => group.roles.length > 0),
  };
}
